import { Component, OnInit, signal } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import {
  GoogleAuthProvider,
  RecaptchaVerifier,
  signInWithEmailAndPassword,
  signInWithPhoneNumber,
  signInWithPopup,
} from 'firebase/auth';
import { CountryISO, NgxIntlTelInputModule } from 'ngx-intl-tel-input';
import { auth, dataStore, db, functions } from 'src/app/app.component';
import { CustomValidators } from 'angular-custom-validators';
import { loginDataInit } from 'src/app/globals';
import { httpsCallable } from 'firebase/functions';
import { arrayUnion, doc, getDoc, setDoc } from 'firebase/firestore';
import { RootUser } from 'src/app/interfaces';
import { MatFormField, MatLabel, MatInput, MatError, MatSuffix } from '@angular/material/input';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { NgClass } from '@angular/common';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    NgxIntlTelInputModule,
    MatError,
    MatIconButton,
    MatSuffix,
    MatTooltip,
    MatIcon,
    MatButton,
    NgClass,
    RouterLink,
  ],
})
export class LoginComponent implements OnInit {
  signInType = signal<'phone' | 'email'>('email');
  signInStatus = signal<'default' | 'verification'>('default');
  emailForm: UntypedFormGroup = new UntypedFormGroup({
    email: new UntypedFormControl('', [CustomValidators.email, Validators.required]),
    password: new UntypedFormControl('', [Validators.minLength(6), Validators.required]),
  });
  phoneForm: UntypedFormGroup = new UntypedFormGroup({
    phoneNumber: new UntypedFormControl('', [Validators.required]),
  });
  verificationForm: UntypedFormGroup = new UntypedFormGroup({
    code: new UntypedFormControl('', [Validators.required]),
  });
  phoneSubmitted = signal(false);
  awaitingResponse = signal(false);
  showPasswords = signal(false);
  CountryISO = CountryISO;
  windowRef: any;
  verificationCode: string;
  captcha = signal(false);
  captchaIsRendering = signal(false);
  verificationError = false;

  loading = signal(true);

  constructor(
    private router: Router,
    private snackBar: MatSnackBar,
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
  ) {}

  ngOnInit(): void {
    this.phoneForm.controls.phoneNumber.valueChanges.subscribe((value) => {
      this.generateCaptcha(value);
      if (this.phoneForm.controls.phoneNumber.hasError('user-not-found')) {
        this.phoneForm.controls.phoneNumber.setErrors({
          'user-not-found': null,
        });
        this.phoneForm.controls.phoneNumber.updateValueAndValidity();
      }
    });
    this.emailForm.controls.email.valueChanges.subscribe((value) => {
      if (this.emailForm.controls.email.hasError('user-not-found')) {
        this.emailForm.controls.email.setErrors({ 'user-not-found': null });
        this.emailForm.controls.email.updateValueAndValidity();
      }
      if (this.emailForm.controls.email.hasError('no-rights')) {
        this.emailForm.controls.email.setErrors({ 'no-rights': null });
        this.emailForm.controls.email.updateValueAndValidity();
      }
    });
    this.emailForm.controls.password.valueChanges.subscribe((value) => {
      if (this.emailForm.controls.email.hasError('wrong-password')) {
        this.emailForm.controls.email.setErrors({ 'wrong-password': null });
        this.emailForm.controls.email.updateValueAndValidity();
      }
    });
    this.verificationForm.controls.code.valueChanges.subscribe((value) => {
      if (this.verificationForm.controls.code.hasError('no-confirmation-result')) {
        this.verificationForm.controls.code.setErrors({
          'no-confirmation-result': null,
        });
        this.verificationForm.controls.code.updateValueAndValidity();
      }
      if (this.verificationForm.controls.code.hasError('invalid-code')) {
        this.verificationForm.controls.code.setErrors({
          'invalid-code': null,
        });
        this.verificationForm.controls.code.updateValueAndValidity();
      }
    });
    this.loading.set(false);
  }

  async signIn(type: string) {
    const loginAuthUnsub = auth.beforeAuthStateChanged(async (user) => {
      try {
        const callable = httpsCallable(functions, 'accountCheckInfo');
        const callableResult = await callable({
          check: 'login',
          orgId: dataStore.selectedOrganisation.id,
          accountData: user?.uid,
        });
        switch (callableResult.data['status']) {
          case 'no-rights':
            throw new Error('no-rights');
          case 'error':
            throw new Error(callableResult.data['message']);
        }
        loginAuthUnsub();
      } catch (error) {
        console.error('Failed pre-login:', error);

        switch (error) {
          case 'no-rights':
            if (type === 'phone') {
              this.phoneForm.controls.phoneNumber.setErrors({
                'no-rights': true,
              });
            } else if (type === 'email') {
              this.emailForm.controls.email.setErrors({ 'no-rights': true });
            }
            break;
          default:
            this.snackBar.open(
              'Er is een onbekende fout opgetreden. Probeer het later opnieuw.',
              'X',
            );
            break;
        }
        await auth.signOut();
        this.awaitingResponse.set(false);
        throw new Error(error);
      }
    });

    switch (type) {
      case 'email':
        this.emailSignIn();
        break;
      case 'phone':
        this.phoneSubmitted.set(true);
        this.phoneSignIn();
        break;
    }
  }

  async emailSignIn() {
    if (!this.emailForm.valid) {
      this.emailForm.markAllAsTouched();
      return;
    }

    const form = this.emailForm.value;
    this.awaitingResponse.set(true);

    try {
      const loginResult = await signInWithEmailAndPassword(auth, form.email, form.password);
      if (auth.authStateReady() && loginResult.user) {
        await loginDataInit(
          loginResult.user,
          this.router,
          false,
          this.iconRegistry,
          this.sanitizer,
        );
      }
    } catch (error) {
      console.error('Error: ', error);
      this.awaitingResponse.set(false);
      switch (error.code) {
        case 'auth/user-not-found':
          this.emailForm.controls.email.setErrors({
            'user-not-found': true,
          });
          break;
        case 'auth/wrong-password':
          this.emailForm.controls.password.setErrors({
            'wrong-password': true,
          });
          break;
        default:
          this.snackBar.open(
            'Er is een onbekende fout opgetreden. Probeer het later opnieuw.',
            'X',
          );
          break;
      }
    }
  }

  async phoneSignIn() {
    if (!this.phoneForm.valid) {
      this.phoneForm.markAllAsTouched();
      return;
    }

    const form = this.phoneForm.value;
    if (form.phoneNumber && form.phoneNumber.e164Number !== '') {
      this.awaitingResponse.set(true);
      try {
        const signIn = await signInWithPhoneNumber(
          auth,
          form.phoneNumber.e164Number,
          this.windowRef.recaptchaVerifier,
        );
        this.signInStatus.set('verification');
        this.windowRef.confirmationResult = signIn;
        this.awaitingResponse.set(false);
        this.snackBar.open(
          'Er is een verificatie code verstuurd naar je telefoon. Dit kan een paar minuten duren.',
          'X',
        );
      } catch (error) {
        console.log('Error: ', error);
        this.awaitingResponse.set(false);
        switch (error.code) {
          case 'auth/user-not-found':
            this.phoneForm.controls.phoneNumber.setErrors({
              'user-not-found': true,
            });
            break;
          default:
            this.snackBar.open(
              'Er is een onbekende fout opgetreden. Probeer het later opnieuw.',
              'X',
            );
            break;
        }
      }
    }
  }

  async verifyLoginCode() {
    if (!this.verificationForm.valid) {
      this.verificationForm.markAllAsTouched();
      return;
    }

    this.awaitingResponse.set(true);
    try {
      if (!this.windowRef.confirmationResult) {
        throw new Error('no-confirmation-result');
      }
      await this.windowRef.confirmationResult.confirm(this.verificationForm.value.code);
      auth.onAuthStateChanged(async (user) => {
        await loginDataInit(user, this.router, false, this.iconRegistry, this.sanitizer);
      });
    } catch (error) {
      console.error('Error: ', error);
      this.awaitingResponse.set(false);
      switch (error) {
        case 'no-confirmation-result':
          this.verificationForm.controls.code.setErrors({
            'no-confirmation-result': true,
          });
          break;
        case 'invalid-code':
          this.verificationForm.controls.code.setErrors({
            'invalid-code': true,
          });
          break;
        default:
          this.snackBar.open(
            'Er is een onbekende fout opgetreden. Probeer het later opnieuw.',
            'X',
          );
          break;
      }
    }
  }

  goBackFromVerification() {
    const phoneValue = this.phoneForm.controls.phoneNumber.value;
    const rawNumber = phoneValue?.e164Number || phoneValue;
    this.phoneForm.controls.phoneNumber.setValue(rawNumber);
    this.phoneForm.controls.phoneNumber.setErrors(null);
    this.phoneForm.controls.phoneNumber.markAsUntouched();
    this.phoneSubmitted.set(false);
    this.signInStatus.set('default');
  }

  generateCaptcha(value: any) {
    if (value && !this.captcha() && !this.captchaIsRendering()) {
      this.captchaIsRendering.set(true);
      this.captcha.set(true);
      setTimeout(() => {
        try {
          this.windowRef = window;
          this.windowRef.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
            size: 'invisible',
          });
          this.windowRef.recaptchaVerifier.render();
        } catch (error) {
          console.error('reCAPTCHA initialization failed', error);
          this.captchaIsRendering.set(false);
          this.captcha.set(false);
        }
      }, 100);
    }
  }

  async googleLogin() {
    try {
      const googlePopUpResult = await signInWithPopup(auth, new GoogleAuthProvider());
      const userRef = doc(db, `users/${googlePopUpResult.user.uid}`);
      const rootUser = (await getDoc(userRef)).data() as RootUser;

      if (!userRef || !rootUser?.organisations) {
        setDoc(
          userRef,
          {
            organisations: [
              {
                id: dataStore.selectedOrganisation.id,
                name: dataStore.selectedOrganisation.name,
              },
            ],
            selectedOrganisation: dataStore.selectedOrganisation.id,
            signInMethod: 'google',
          },
          { merge: true },
        );
      } else {
        const userUpdateObj = {
          selectedOrgId: dataStore.selectedOrganisation.id,
        } as any;
        if (rootUser && rootUser.organisations) {
          if (!rootUser.organisations.find((x) => x.id === dataStore.selectedOrganisation.id)) {
            userUpdateObj.organisations = arrayUnion({
              id: dataStore.selectedOrganisation.id,
              name: dataStore.selectedOrganisation.name,
            });
          }
        }
        await setDoc(userRef, userUpdateObj, { merge: true });
      }
      await loginDataInit(
        googlePopUpResult.user,
        this.router,
        false,
        this.iconRegistry,
        this.sanitizer,
      );
    } catch (error) {
      console.log(error);
    }
  }
}
